import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import type {
  AiMailResult,
  EmailDetail,
  EmailSummary,
  EmailUpdateRequest,
  MailFolder,
  SentEmail,
  SwipeAction,
} from "@websede/contracts";
import DOMPurify from "dompurify";
import {
  Archive,
  ArrowLeft,
  Ban,
  Check,
  ChevronDown,
  Download,
  File,
  FileText,
  Folder,
  FolderInput,
  Forward,
  Inbox,
  ListChecks,
  Mail,
  MailOpen,
  OctagonAlert,
  Paperclip,
  Pencil,
  RefreshCw,
  Reply,
  Search,
  Send,
  Sparkles,
  Star,
  StickyNote,
  Trash2,
  X,
} from "lucide-react";
import {
  type ComponentType,
  type FormEvent,
  type ReactNode,
  type TouchEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client.js";
import { useSession } from "../auth/session.js";
import { Avatar } from "../components/avatar.js";
import { useEntitlements } from "../components/billing-banner.js";
import { ErrorNotice } from "../components/form.js";
import { ShellSearch, ShellSidebar, useShell } from "../components/shell-slots.js";
import { useFeatures } from "../features.js";
import {
  ComposePane,
  type ComposeState,
  type ComposeUpdate,
  composeFromDraft,
  signatureBlock,
  UndoSendBar,
} from "../mail/compose.js";
import { offlineCacheActive, useOnline } from "../pwa.js";

const PAGE = 50;
const EDITABLE = /^(INPUT|TEXTAREA|SELECT)$/;
/** How far a row has to travel on a phone before letting go archives it. */
const SWIPE_ACTION_PX = 96;
/** Below Tailwind's md breakpoint the list uses the phone layout. */
const PHONE_QUERY = "(max-width: 767.98px)";

type SelectWhich = "all" | "none" | "read" | "unread" | "starred" | "unstarred";
const SELECT_WHICH: SelectWhich[] = ["all", "none", "read", "unread", "starred", "unstarred"];

type Icon = ComponentType<{ className?: string; strokeWidth?: number }>;

const FOLDER_ICONS: Record<string, Icon> = {
  inbox: Inbox,
  sent: Send,
  drafts: File,
  archive: Archive,
  junk: Ban,
  trash: Trash2,
};
const FOLDER_ORDER = ["inbox", "sent", "drafts", "archive", "junk", "trash"];

/** Today's mail shows the time, this year's the day and month, older mail the full date. */
export function shortWhen(iso: string, locale: string, now = new Date()): string {
  const date = new Date(iso);
  if (date.toDateString() === now.toDateString()) {
    return new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).format(date);
  }
  if (date.getFullYear() === now.getFullYear()) {
    return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" }).format(date);
  }
  return new Intl.DateTimeFormat(locale, { dateStyle: "short" }).format(date);
}

function senderOf(email: { from: Array<{ name?: string | null; email: string }> }): string {
  return email.from[0]?.name || email.from[0]?.email || "";
}

/** Gmail's layout: folders on the left, the list across the page, a message opens full width. */
export function MailPage() {
  const { t } = useTranslation();
  const session = useSession();
  const queryClient = useQueryClient();
  const { closeDrawer } = useShell();
  const account = useQuery({ queryKey: ["mail", "account"], queryFn: () => api.mail.account() });
  const ownAddress = account.data?.mailbox?.address;
  const aliases = (account.data?.mailbox?.aliases ?? []).map((a) => a.address);
  const senders = ownAddress ? [ownAddress, ...aliases] : [];
  const hasMailbox = Boolean(account.data?.mailbox);
  const folders = useQuery({
    queryKey: ["mail", "folders"],
    queryFn: () => api.mail.folders(),
    enabled: hasMailbox,
    refetchInterval: 30_000,
  });
  const [folderId, setFolderId] = useState<string | null>(null);
  const deepLink = useSearch({ from: "/app/mail" });
  const [openId, setOpenId] = useState<string | null>(deepLink.email ?? null);
  const [compose, setCompose] = useState<ComposeState | null>(null);
  /** Changes only when a new composer opens, so saving a draft never remounts it. */
  const [composeSession, setComposeSession] = useState(0);
  const openCompose = useCallback((state: ComposeState) => {
    setComposeSession((n) => n + 1);
    setCompose(state);
  }, []);
  /** The bar after a send: undo while it is held, and who the mail server refused. */
  const [undo, setUndo] = useState<{
    key: number;
    submissionId: string | null;
    sendAt: string | null;
    refused: string[];
  } | null>(null);
  const mailSettings = useQuery({
    queryKey: ["mail", "settings"],
    queryFn: () => api.mail.settings(),
    enabled: hasMailbox,
  });
  const signature = mailSettings.data?.signature ?? "";
  const updateCompose: ComposeUpdate = useCallback(
    (update) => setCompose((current) => (current ? update(current) : current)),
    [],
  );
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [live, setLive] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const sortedFolders = useMemo(() => {
    const list = folders.data?.folders ?? [];
    const rank = (f: MailFolder) => {
      const index = f.role ? FOLDER_ORDER.indexOf(f.role) : -1;
      return index === -1 ? FOLDER_ORDER.length : index;
    };
    return [...list].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
  }, [folders.data]);

  useEffect(() => {
    if (!folderId && folders.data) {
      const inbox = folders.data.folders.find((f) => f.role === "inbox") ?? folders.data.folders[0];
      if (inbox) setFolderId(inbox.id);
    }
  }, [folderId, folders.data]);

  const emails = useInfiniteQuery({
    queryKey: ["mail", "emails", folderId, query],
    queryFn: ({ pageParam }) => api.mail.emails(folderId ?? "", pageParam, PAGE, query),
    initialPageParam: 0,
    getNextPageParam: (last) => {
      const next = last.position + last.emails.length;
      return last.emails.length > 0 && next < last.total ? next : undefined;
    },
    enabled: Boolean(folderId),
    refetchInterval: live ? false : 30_000,
  });
  const list = useMemo(() => emails.data?.pages.flatMap((p) => p.emails) ?? [], [emails.data]);
  const total = emails.data?.pages[0]?.total ?? 0;
  const folder = folders.data?.folders.find((f) => f.id === folderId);
  const folderRole = folder?.role;
  const archive = folders.data?.folders.find((f) => f.role === "archive");
  const trash = folders.data?.folders.find((f) => f.role === "trash");
  const junk = folders.data?.folders.find((f) => f.role === "junk");
  const inbox = folders.data?.folders.find((f) => f.role === "inbox");

  // The selection belongs to one list: another folder or search starts empty, and messages that
  // left the list (moved, deleted) drop out of it.
  const listKey = `${folderId ?? ""}\u0000${query}`;
  const [selection, setSelection] = useState<{ key: string; ids: ReadonlySet<string> }>({
    key: "",
    ids: new Set(),
  });
  const chosen = useMemo(
    () => (selection.key === listKey ? list.filter((e) => selection.ids.has(e.id)) : []),
    [selection, listKey, list],
  );
  const select = useCallback(
    (ids: Iterable<string>) => setSelection({ key: listKey, ids: new Set(ids) }),
    [listKey],
  );
  const toggleSelected = useCallback(
    (id: string) => {
      const next = new Set(chosen.map((e) => e.id));
      if (next.has(id)) next.delete(id);
      else next.add(id);
      select(next);
    },
    [chosen, select],
  );
  const selecting = chosen.length > 0;
  useEffect(() => {
    if (!selecting) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") select([]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selecting, select]);

  // Keep the newest inbox messages readable offline by fetching their bodies ahead of time.
  // Only when a service worker keeps copies; reading a message does not mark it as read.
  useEffect(() => {
    if (folderRole !== "inbox" || query || !offlineCacheActive()) return;
    for (const email of list.slice(0, 20)) {
      void queryClient.prefetchQuery({
        queryKey: ["mail", "email", email.id],
        queryFn: () => api.mail.email(email.id),
        staleTime: 5 * 60_000,
      });
    }
  }, [folderRole, query, list, queryClient]);

  const refreshLists = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["mail", "folders"] });
    void queryClient.invalidateQueries({ queryKey: ["mail", "emails"] });
  }, [queryClient]);

  // Live updates: the API relays the mail server's change notifications as server-sent events.
  useEffect(() => {
    if (!hasMailbox || typeof EventSource === "undefined") return;
    let source: EventSource | null = null;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let failures = 0;
    let connectedBefore = false;
    const connect = () => {
      const current = new EventSource(api.mail.eventsUrl(), { withCredentials: true });
      source = current;
      current.onopen = () => {
        failures = 0;
        setLive(true);
        // Changes made while the stream was down were not announced.
        if (connectedBefore) refreshLists();
        connectedBefore = true;
      };
      current.onerror = () => {
        setLive(false);
        // The browser retries a dropped stream by itself, but gives up for good when a retry
        // gets an error answer (the proxy's 502 while a deploy replaces the API). Start over
        // after a growing, slightly random pause so reconnecting tabs do not arrive together.
        if (current.readyState !== EventSource.CLOSED) return;
        const delay = Math.min(30_000, 1_000 * 2 ** failures) * (0.75 + Math.random() / 2);
        failures += 1;
        retry = setTimeout(connect, delay);
      };
      current.addEventListener("change", () => {
        refreshLists();
        void queryClient.invalidateQueries({ queryKey: ["mail", "email"] });
      });
    };
    connect();
    return () => {
      if (retry) clearTimeout(retry);
      source?.close();
      setLive(false);
    };
  }, [hasMailbox, refreshLists, queryClient]);

  const quickUpdate = useMutation({
    mutationFn: ({ id, change }: { id: string; change: EmailUpdateRequest }) =>
      api.mail.update(id, change),
    onSuccess: (_, { id }) => {
      refreshLists();
      void queryClient.invalidateQueries({ queryKey: ["mail", "email", id] });
    },
  });
  const batch = useMutation({
    mutationFn: (change: EmailUpdateRequest) =>
      api.mail.updateMany({ ids: chosen.map((e) => e.id), change }),
    onSuccess: () => {
      refreshLists();
      void queryClient.invalidateQueries({ queryKey: ["mail", "email"] });
    },
  });
  const moveChosen = (target: MailFolder | undefined) => {
    if (target) batch.mutate({ mailboxId: target.id });
  };
  const pick = (which: SelectWhich) =>
    select(
      list
        .filter((e) =>
          which === "all"
            ? true
            : which === "read"
              ? e.seen
              : which === "unread"
                ? !e.seen
                : which === "starred"
                  ? e.flagged
                  : which === "unstarred"
                    ? !e.flagged
                    : false,
        )
        .map((e) => e.id),
    );
  const toggleStar = useCallback(
    (email: EmailSummary) =>
      quickUpdate.mutate({ id: email.id, change: { flagged: !email.flagged } }),
    [quickUpdate],
  );
  // Swipe actions from the settings; one that makes no sense in this folder does nothing.
  const swipeFor = (action: SwipeAction | undefined): SwipeAction =>
    (action === "archive" && (!archive || folderRole === "archive")) ||
    (action === "delete" && (!trash || folderRole === "trash"))
      ? "none"
      : (action ?? "archive");
  const swipe = {
    left: swipeFor(mailSettings.data?.swipeLeft),
    right: swipeFor(mailSettings.data?.swipeRight),
  };
  const onSwipe = (email: EmailSummary, action: SwipeAction) => {
    if (action === "archive" && archive) {
      quickUpdate.mutate({ id: email.id, change: { mailboxId: archive.id } });
    } else if (action === "delete" && trash) {
      quickUpdate.mutate({ id: email.id, change: { mailboxId: trash.id } });
    } else if (action === "read") {
      quickUpdate.mutate({ id: email.id, change: { seen: !email.seen } });
    } else if (action === "star") {
      toggleStar(email);
    }
  };

  const openRelative = useCallback(
    (delta: number) => {
      if (list.length === 0) return;
      const index = openId ? list.findIndex((e) => e.id === openId) : -1;
      const next = Math.min(Math.max(index + delta, 0), list.length - 1);
      const target = list[next];
      if (target) setOpenId(target.id);
    },
    [list, openId],
  );
  const startCompose = useCallback(
    () => openCompose({ to: "", subject: "", text: signatureBlock(signature) }),
    [signature, openCompose],
  );
  // "Send email" elsewhere in the suite (a contact card) arrives as /mail?to=address.
  const navigate = useNavigate();
  const composeTo = deepLink.to;
  useEffect(() => {
    if (!composeTo || !mailSettings.isFetched) return;
    openCompose({ to: composeTo, subject: "", text: signatureBlock(signature) });
    void navigate({ to: "/mail", search: {}, replace: true });
  }, [composeTo, mailSettings.isFetched, signature, openCompose, navigate]);
  /** Opens a saved draft in the composer instead of the reading view. */
  const openDraft = useCallback(
    async (id: string) => {
      const draft = await api.mail.email(id);
      setOpenId(null);
      openCompose(composeFromDraft(draft));
    },
    [openCompose],
  );
  const onSent = (result: SentEmail) => {
    setCompose(null);
    refreshLists();
    const held = result.submissionId !== null && result.sendAt !== null;
    const refused = result.refused.map((r) => r.email);
    if (held || refused.length > 0) {
      setUndo({
        key: Date.now(),
        submissionId: held ? result.submissionId : null,
        sendAt: held ? result.sendAt : null,
        refused,
      });
    }
  };
  const closeOpen = useCallback(() => setOpenId(null), []);
  const focusSearch = useCallback(() => searchRef.current?.focus(), []);
  const onNext = useCallback(() => openRelative(1), [openRelative]);
  const onPrev = useCallback(() => openRelative(-1), [openRelative]);
  const onOpen = useCallback(() => openRelative(0), [openRelative]);
  const selectFolder = (id: string) => {
    setFolderId(id);
    setOpenId(null);
    setQuery("");
    setSearch("");
    closeDrawer();
  };

  const onSearch = (event: FormEvent) => {
    event.preventDefault();
    setQuery(search.trim());
    setOpenId(null);
  };

  if (account.isSuccess && !hasMailbox) {
    const isAdmin = session.data?.user.role === "owner" || session.data?.user.role === "admin";
    return (
      <section className="mx-auto flex max-w-md flex-col items-center gap-3 p-10 text-center">
        <Inbox className="size-12 text-ink-3" strokeWidth={1.5} />
        <h1 className="text-xl">{t("mail.noMailbox")}</h1>
        {isAdmin ? (
          <Link
            to="/admin/users"
            className="rounded-full bg-accent px-5 py-2 text-sm font-medium text-white"
          >
            {t("mail.createInConsole")}
          </Link>
        ) : (
          <p className="text-sm text-ink-3">{t("mail.askAdmin")}</p>
        )}
      </section>
    );
  }

  const folderLabel = (f: MailFolder) => (f.role ? t(`mail.folders.${f.role}`) : f.name);

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      <ShellSearch>
        <form onSubmit={onSearch} className="flex h-full min-w-0 flex-1 items-center">
          <button
            type="submit"
            aria-label={t("mail.searchMail")}
            className="hidden size-12 shrink-0 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5 md:flex"
          >
            <Search className="size-5" />
          </button>
          <input
            ref={searchRef}
            type="search"
            className="h-full min-w-0 flex-1 bg-transparent px-2 text-base outline-none placeholder:text-ink-2 md:px-1"
            placeholder={t("mail.searchMail")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {query || search ? (
            <button
              type="button"
              aria-label={t("mail.clearSearch")}
              className="flex size-10 shrink-0 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5"
              onClick={() => {
                setSearch("");
                setQuery("");
              }}
            >
              <X className="size-5" />
            </button>
          ) : null}
        </form>
      </ShellSearch>

      <ShellSidebar>
        <div className="hidden px-2 pt-1 pb-4 md:block">
          <button
            type="button"
            onClick={startCompose}
            className="flex h-14 items-center gap-3 rounded-2xl bg-accent-soft pr-6 pl-4 text-sm font-medium text-accent hover:shadow-1"
          >
            <Pencil className="size-5" />
            {t("mail.compose")}
          </button>
        </div>
        <nav aria-label={t("nav.mail")} className="flex flex-col pr-2 md:pr-3">
          {sortedFolders.map((f) => {
            const FolderIcon = (f.role && FOLDER_ICONS[f.role]) || Folder;
            const active = f.id === folderId;
            const unread = f.unreadEmails > 0 && f.role !== "sent" && f.role !== "trash";
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => selectFolder(f.id)}
                className={`flex h-9 items-center gap-4 rounded-r-full pr-3 pl-6 text-left text-sm ${active ? "bg-accent-soft font-medium text-accent" : "text-ink-2 hover:bg-ink/5"} ${unread && !active ? "font-medium text-ink" : ""}`}
              >
                <FolderIcon className="size-[18px] shrink-0" strokeWidth={active ? 2.2 : 1.8} />
                <span className="min-w-0 flex-1 truncate">{folderLabel(f)}</span>
                {unread ? <span className="text-xs tabular-nums">{f.unreadEmails}</span> : null}
              </button>
            );
          })}
        </nav>
        <div className="mt-4 px-6 text-xs text-ink-3">
          <p className="truncate" title={account.data?.mailbox?.address}>
            {account.data?.mailbox?.address}
          </p>
          <p className="mt-0.5 flex items-center gap-1.5">
            <span className={`size-1.5 rounded-full ${live ? "bg-good" : "bg-ink-3"}`} />
            {live ? t("mail.live") : t("mail.polling")}
          </p>
        </div>
      </ShellSidebar>

      {openId ? (
        <ReadingView
          id={openId}
          folders={folders.data?.folders ?? []}
          position={list.findIndex((e) => e.id === openId)}
          total={total}
          onChanged={refreshLists}
          onClosed={closeOpen}
          onNewer={onPrev}
          onOlder={onNext}
          onReply={(email, draft) => openCompose(replyTo(email, draft, signature, aliases))}
          onForward={(email) => openCompose(forwardOf(email, signature, t))}
        />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex h-12 shrink-0 items-center gap-0.5 px-2 md:px-4">
            {selecting ? (
              <span className="md:hidden">
                <ToolbarButton
                  label={t("mail.clearSelection")}
                  icon={X}
                  onClick={() => select([])}
                />
              </span>
            ) : null}
            {list.length > 0 ? (
              <SelectMenu
                state={
                  chosen.length === 0 ? "none" : chosen.length === list.length ? "all" : "some"
                }
                onPick={pick}
              />
            ) : null}
            {selecting ? (
              <>
                <span className="px-2 text-sm font-medium text-ink md:hidden">{chosen.length}</span>
                <span className="flex-1 md:hidden" />
                {archive && folderRole !== "archive" ? (
                  <ToolbarButton
                    label={t("mail.archive")}
                    icon={Archive}
                    disabled={batch.isPending}
                    onClick={() => moveChosen(archive)}
                  />
                ) : null}
                {folderRole === "junk" ? (
                  <ToolbarButton
                    label={t("mail.notSpam")}
                    icon={Inbox}
                    disabled={batch.isPending}
                    onClick={() => moveChosen(inbox)}
                  />
                ) : junk ? (
                  <ToolbarButton
                    label={t("mail.reportSpam")}
                    icon={OctagonAlert}
                    disabled={batch.isPending}
                    onClick={() => moveChosen(junk)}
                  />
                ) : null}
                {folderRole === "trash" ? (
                  <ToolbarButton
                    label={t("mail.moveToInbox")}
                    icon={Inbox}
                    disabled={batch.isPending}
                    onClick={() => moveChosen(inbox)}
                  />
                ) : trash ? (
                  <ToolbarButton
                    label={t("mail.delete")}
                    icon={Trash2}
                    disabled={batch.isPending}
                    onClick={() => moveChosen(trash)}
                  />
                ) : null}
                <span className="mx-1 hidden h-5 w-px bg-rule md:block" />
                {chosen.some((e) => !e.seen) ? (
                  <ToolbarButton
                    label={t("mail.markRead")}
                    icon={MailOpen}
                    disabled={batch.isPending}
                    onClick={() => batch.mutate({ seen: true })}
                  />
                ) : (
                  <ToolbarButton
                    label={t("mail.markUnread")}
                    icon={Mail}
                    disabled={batch.isPending}
                    onClick={() => batch.mutate({ seen: false })}
                  />
                )}
                <ToolbarButton
                  label={chosen.every((e) => e.flagged) ? t("mail.unstar") : t("mail.star")}
                  icon={Star}
                  disabled={batch.isPending}
                  onClick={() => batch.mutate({ flagged: !chosen.every((e) => e.flagged) })}
                />
                <DropMenu
                  label={t("mail.moveTo")}
                  trigger={<FolderInput className="size-5" />}
                  triggerClassName="flex size-10 shrink-0 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5"
                  align="right-0 md:left-0 md:right-auto"
                  items={sortedFolders
                    .filter((f) => f.id !== folderId && f.role !== "drafts" && f.role !== "sent")
                    .map((f) => ({
                      key: f.id,
                      label: folderLabel(f),
                      onSelect: () => moveChosen(f),
                    }))}
                />
                <span className="hidden flex-1 md:block" />
                <span className="hidden pr-2 text-xs text-ink-3 md:block">
                  {t("mail.selectedCount", { count: chosen.length })}
                </span>
              </>
            ) : (
              <>
                <span className="px-2 text-sm font-medium text-ink-2 md:hidden">
                  {query ? t("mail.results") : folder ? folderLabel(folder) : ""}
                </span>
                <button
                  type="button"
                  aria-label={t("mail.refresh")}
                  title={t("mail.refresh")}
                  className="hidden size-10 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5 md:flex"
                  onClick={refreshLists}
                >
                  <RefreshCw className={`size-[18px] ${emails.isFetching ? "animate-spin" : ""}`} />
                </button>
                <span className="flex-1" />
                {emails.data && total > 0 ? (
                  <span className="text-xs text-ink-3">
                    {t("mail.range", { from: 1, to: list.length, total })}
                  </span>
                ) : null}
              </>
            )}
          </div>
          <ErrorNotice error={emails.error ?? quickUpdate.error ?? batch.error} />
          <ul className="min-h-0 flex-1 overflow-auto pb-24 md:pb-4">
            {list.map((e) => (
              <MessageRow
                key={e.id}
                email={e}
                folderRole={folderRole}
                swipe={swipe}
                selected={selection.key === listKey && selection.ids.has(e.id)}
                selecting={selecting}
                onSelect={() => toggleSelected(e.id)}
                onOpen={() => (folderRole === "drafts" ? void openDraft(e.id) : setOpenId(e.id))}
                onStar={() => toggleStar(e)}
                onSwipe={(action) => onSwipe(e, action)}
              />
            ))}
            {emails.data && total === 0 ? (
              <li className="flex flex-col items-center gap-2 px-4 py-16 text-sm text-ink-3">
                <Inbox className="size-10" strokeWidth={1.4} />
                {query ? t("mail.noResults") : t("mail.empty")}
              </li>
            ) : null}
            {emails.hasNextPage ? (
              <li className="flex justify-center p-3">
                <button
                  type="button"
                  className="h-9 rounded-full border border-rule px-4 text-sm text-ink-2 hover:bg-surface-2"
                  disabled={emails.isFetchingNextPage}
                  onClick={() => emails.fetchNextPage()}
                >
                  {t("mail.loadOlder")}
                </button>
              </li>
            ) : null}
          </ul>
        </div>
      )}

      {openId || compose ? null : (
        <button
          type="button"
          onClick={startCompose}
          className="absolute right-4 bottom-4 flex h-14 items-center gap-3 rounded-2xl bg-accent-soft px-5 text-sm font-medium text-accent shadow-2 md:hidden"
        >
          <Pencil className="size-5" />
          {t("mail.compose")}
        </button>
      )}

      {compose ? (
        <ComposePane
          key={composeSession}
          state={compose}
          onChange={updateCompose}
          onClose={() => {
            setCompose(null);
            refreshLists();
          }}
          onSent={onSent}
          undoSeconds={mailSettings.data?.undoSeconds ?? 10}
          senders={senders}
        />
      ) : null}
      {undo ? (
        <UndoSendBar
          key={undo.key}
          submissionId={undo.submissionId}
          sendAt={undo.sendAt}
          refused={undo.refused}
          onDismiss={() => setUndo(null)}
          onUndone={(draftId) => {
            setUndo(null);
            refreshLists();
            void openDraft(draftId);
          }}
        />
      ) : null}
      <Shortcuts
        enabled={!compose}
        onNext={onNext}
        onPrev={onPrev}
        onOpen={onOpen}
        onCompose={startCompose}
        onClose={closeOpen}
        onSearch={focusSearch}
      />
    </div>
  );
}

function replyTo(
  email: EmailDetail,
  draft = "",
  signature = "",
  aliases: string[] = [],
): ComposeState {
  const quoted = (email.textBody ?? "").split("\n").join("\n> ");
  // A message that came in on an alias is answered from that alias, as Gmail does.
  const from = aliasOf(email, aliases);
  return {
    ...(from ? { from } : {}),
    to: (email.replyTo.length > 0 ? email.replyTo : email.from).map((a) => a.email).join(", "),
    subject: email.subject.startsWith("Re:") ? email.subject : `Re: ${email.subject}`,
    // The signature sits under the reply, above the quoted message.
    text: `${draft}${signatureBlock(signature)}\n\n> ${quoted}`,
    ...(email.messageId
      ? { inReplyTo: email.messageId, references: [...(email.inReplyTo ?? []), ...email.messageId] }
      : {}),
  };
}

/** The alias a message was addressed to, if any. */
function aliasOf(email: EmailDetail, aliases: string[]): string | undefined {
  const recipients = [...email.to, ...email.cc].map((a) => a.email.toLowerCase());
  return aliases.find((alias) => recipients.includes(alias));
}

/** Forwards the text with the usual header block; the attachments travel along by reference. */
function forwardOf(
  email: EmailDetail,
  signature: string,
  t: (key: string) => string,
): ComposeState {
  const address = (a: { name?: string | null; email: string }) =>
    a.name ? `${a.name} <${a.email}>` : a.email;
  const header = [
    `---------- ${t("mail.forwardedHeader")} ----------`,
    `${t("mail.from")}: ${email.from.map(address).join(", ")}`,
    `${t("mail.date")}: ${new Date(email.receivedAt).toUTCString()}`,
    `${t("mail.subject")}: ${email.subject}`,
    `${t("mail.to")}: ${email.to.map(address).join(", ")}`,
  ].join("\n");
  return {
    to: "",
    subject: /^(fwd?|ilt):/i.test(email.subject) ? email.subject : `Fwd: ${email.subject}`,
    text: `${signatureBlock(signature)}\n\n${header}\n\n${email.textBody ?? ""}`,
    attachments: email.attachments.map((a) => ({
      blobId: a.blobId,
      name: a.name ?? "attachment",
      type: a.type,
      size: a.size,
    })),
  };
}

/** Gmail-style single-key shortcuts; ignored while typing in a field. */
function Shortcuts({
  enabled,
  onNext,
  onPrev,
  onOpen,
  onCompose,
  onClose,
  onSearch,
}: {
  enabled: boolean;
  onNext: () => void;
  onPrev: () => void;
  onOpen: () => void;
  onCompose: () => void;
  onClose: () => void;
  onSearch: () => void;
}) {
  useEffect(() => {
    if (!enabled) return;
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && EDITABLE.test(target.tagName)) {
        if (event.key === "Escape") target.blur();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const actions: Record<string, () => void> = {
        j: onNext,
        k: onPrev,
        Enter: onOpen,
        c: onCompose,
        "/": onSearch,
        Escape: onClose,
      };
      const action = actions[event.key];
      if (action) {
        event.preventDefault();
        action();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [enabled, onNext, onPrev, onOpen, onCompose, onClose, onSearch]);
  return null;
}

/**
 * One message in the list. Phones get the two-line card with the sender's initial and swipe to
 * archive; wider screens get Gmail's single line with star, sender, subject and preview.
 */
function MessageRow({
  email,
  folderRole,
  swipe,
  selected,
  selecting,
  onSelect,
  onOpen,
  onStar,
  onSwipe,
}: {
  email: EmailSummary;
  folderRole: string | null | undefined;
  /** What swiping right and left does; "none" keeps the row still. */
  swipe: { left: SwipeAction; right: SwipeAction };
  selected: boolean;
  /** Something in the list is selected: on phones a tap then selects instead of opening. */
  selecting: boolean;
  onSelect: () => void;
  onOpen: () => void;
  onStar: () => void;
  onSwipe: (action: SwipeAction) => void;
}) {
  const { t, i18n } = useTranslation();
  const [dx, setDx] = useState(0);
  const touch = useRef<{ x: number; y: number; swiping: boolean } | null>(null);
  const swipedRef = useRef(false);
  const when = shortWhen(email.receivedAt, i18n.language);
  // In Sent and Drafts the other party is the recipient.
  const outgoing = folderRole === "sent" || folderRole === "drafts";
  const who = outgoing
    ? email.to.map((a) => a.name || a.email).join(", ") || t("mail.noRecipient")
    : senderOf(email);
  const unread = !email.seen;
  const subject = email.subject || t("mail.noSubject");

  const onTouchStart = (event: TouchEvent) => {
    const point = event.touches[0];
    swipedRef.current = false;
    const canSwipe = swipe.left !== "none" || swipe.right !== "none";
    if (canSwipe && point) touch.current = { x: point.clientX, y: point.clientY, swiping: false };
  };
  const onTouchMove = (event: TouchEvent) => {
    const start = touch.current;
    const point = event.touches[0];
    if (!start || !point) return;
    const moveX = point.clientX - start.x;
    if (
      !start.swiping &&
      Math.abs(moveX) > 12 &&
      Math.abs(moveX) > Math.abs(point.clientY - start.y)
    ) {
      start.swiping = true;
    }
    if (start.swiping) setDx((moveX > 0 ? swipe.right : swipe.left) === "none" ? 0 : moveX);
  };
  const onTouchEnd = () => {
    swipedRef.current = Boolean(touch.current?.swiping);
    const action = dx > 0 ? swipe.right : swipe.left;
    const swiped = Math.abs(dx) >= SWIPE_ACTION_PX;
    touch.current = null;
    setDx(0);
    if (swiped && action !== "none") onSwipe(action);
  };
  const onTouchCancel = () => {
    touch.current = null;
    setDx(0);
  };
  const swiping = dx > 0 ? swipe.right : swipe.left;
  const SwipeIcon =
    swiping === "delete"
      ? Trash2
      : swiping === "read"
        ? email.seen
          ? Mail
          : MailOpen
        : swiping === "star"
          ? Star
          : Archive;
  const swipeColor =
    swiping === "delete"
      ? "bg-danger"
      : swiping === "read"
        ? "bg-accent"
        : swiping === "star"
          ? "bg-[#f4b400]"
          : "bg-good";

  const star = (
    <button
      type="button"
      aria-label={email.flagged ? t("mail.unstar") : t("mail.star")}
      aria-pressed={email.flagged}
      className="relative z-10 flex size-9 shrink-0 items-center justify-center rounded-full hover:bg-ink/5"
      onClick={(event) => {
        event.stopPropagation();
        onStar();
      }}
    >
      <Star
        className={`size-[18px] ${email.flagged ? "fill-[#f4b400] text-[#f4b400]" : "text-ink-3"}`}
      />
    </button>
  );

  return (
    <li className="relative overflow-hidden">
      {dx !== 0 ? (
        <div
          className={`absolute inset-0 flex items-center px-6 text-white ${swipeColor} ${dx > 0 ? "justify-start" : "justify-end"}`}
        >
          <SwipeIcon className="size-5" />
        </div>
      ) : null}
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchCancel}
        style={dx ? { transform: `translateX(${dx}px)` } : undefined}
        className={`relative flex gap-3 px-4 py-2.5 md:items-center md:gap-1 md:border-b md:border-rule md:px-2 md:py-0 md:hover:z-10 md:hover:shadow-1 ${selected ? "bg-accent-soft" : unread ? "bg-surface" : "bg-surface md:bg-surface-2/60"}`}
      >
        <button
          type="button"
          aria-label={`${who}: ${subject}`}
          className="absolute inset-0 cursor-pointer"
          onClick={() => {
            if (swipedRef.current) return;
            if (selecting && window.matchMedia(PHONE_QUERY).matches) onSelect();
            else onOpen();
          }}
        />
        <button
          type="button"
          aria-label={t("mail.selectMessage", { subject })}
          aria-pressed={selected}
          className="relative z-10 mt-0.5 size-10 shrink-0 self-start rounded-full md:hidden"
          onClick={onSelect}
        >
          {selected ? (
            <span className="flex size-10 items-center justify-center rounded-full bg-accent text-white">
              <Check className="size-5" />
            </span>
          ) : (
            <Avatar name={who} size={40} />
          )}
        </button>
        <div className="pointer-events-none min-w-0 flex-1 md:hidden">
          <div className="flex items-baseline gap-2">
            <span
              className={`min-w-0 flex-1 truncate ${unread ? "font-semibold text-ink" : "text-ink-2"}`}
            >
              {who}
            </span>
            <span
              className={`shrink-0 text-xs ${unread ? "font-semibold text-ink" : "text-ink-3"}`}
            >
              {when}
            </span>
          </div>
          <div className={`truncate text-sm ${unread ? "font-semibold text-ink" : "text-ink-2"}`}>
            {subject}
          </div>
          <div className="flex items-center gap-1">
            <span className="min-w-0 flex-1 truncate text-sm text-ink-3">{email.preview}</span>
            <span className="pointer-events-auto -mr-2">{star}</span>
          </div>
          {email.hasAttachment ? (
            <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-rule px-2 py-0.5 text-xs text-ink-2">
              <Paperclip className="size-3" />
              {t("mail.attachments")}
            </span>
          ) : null}
        </div>

        <label className="relative z-10 hidden size-9 shrink-0 cursor-pointer items-center justify-center rounded-full hover:bg-ink/5 md:flex">
          <input
            type="checkbox"
            checked={selected}
            onChange={onSelect}
            aria-label={t("mail.selectMessage", { subject })}
            className="size-4 cursor-pointer accent-accent"
          />
        </label>
        <span className="hidden md:block">{star}</span>
        <span
          className={`hidden w-52 shrink-0 truncate pr-4 text-sm md:block ${unread ? "font-semibold text-ink" : "text-ink-2"}`}
        >
          {who}
        </span>
        <span className="hidden h-10 min-w-0 flex-1 items-center truncate text-sm md:flex">
          <span className={`truncate ${unread ? "font-semibold text-ink" : "text-ink-2"}`}>
            {subject}
            <span className="font-normal text-ink-3"> – {email.preview}</span>
          </span>
        </span>
        {email.hasAttachment ? (
          <Paperclip className="hidden size-4 shrink-0 text-ink-3 md:block" />
        ) : null}
        <span
          className={`hidden w-20 shrink-0 pr-3 text-right text-xs md:block ${unread ? "font-semibold text-ink" : "text-ink-3"}`}
        >
          {when}
        </span>
      </div>
    </li>
  );
}

/** Gmail's select-all box: a click selects everything or nothing, the arrow picks a subset. */
function SelectMenu({
  state,
  onPick,
}: {
  state: "none" | "some" | "all";
  onPick: (which: SelectWhich) => void;
}) {
  const { t } = useTranslation();
  const box = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (box.current) box.current.indeterminate = state === "some";
  }, [state]);
  return (
    <span className="hidden items-center rounded-md hover:bg-ink/5 md:flex">
      <label className="flex size-9 cursor-pointer items-center justify-center">
        <input
          ref={box}
          type="checkbox"
          checked={state === "all"}
          onChange={() => onPick(state === "none" ? "all" : "none")}
          aria-label={t("mail.select")}
          className="size-4 cursor-pointer accent-accent"
        />
      </label>
      <DropMenu
        label={t("mail.selectOptions")}
        trigger={<ChevronDown className="size-4" />}
        triggerClassName="-ml-2 flex h-9 w-5 items-center justify-center text-ink-2"
        items={SELECT_WHICH.map((which) => ({
          key: which,
          label: t(`mail.selectWhich.${which}`),
          onSelect: () => onPick(which),
        }))}
      />
    </span>
  );
}

/** A button that opens a short list of choices; outside clicks and Escape close it. */
function DropMenu({
  label,
  trigger,
  triggerClassName,
  items,
  align = "left-0",
}: {
  label: string;
  trigger: ReactNode;
  triggerClassName: string;
  items: Array<{ key: string; label: string; onSelect: () => void }>;
  align?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open]);
  return (
    <span className="relative">
      <button
        type="button"
        aria-label={label}
        title={label}
        aria-expanded={open}
        className={triggerClassName}
        onClick={() => setOpen((v) => !v)}
      >
        {trigger}
      </button>
      {open ? (
        <>
          <button
            type="button"
            aria-label={t("common.close")}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            className={`absolute top-full z-50 mt-1 flex max-h-80 min-w-44 flex-col overflow-auto rounded-xl bg-surface py-2 shadow-2 ${align}`}
          >
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                className="h-9 px-4 text-left text-sm text-ink hover:bg-ink/5"
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </span>
  );
}

function ToolbarButton({
  label,
  onClick,
  icon: IconComponent,
  disabled = false,
}: {
  label: string;
  onClick: () => void;
  icon: Icon;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-10 shrink-0 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5 disabled:opacity-40"
    >
      <IconComponent className="size-5" />
    </button>
  );
}

function ReadingView({
  id,
  folders,
  position,
  total,
  onChanged,
  onClosed,
  onNewer,
  onOlder,
  onReply,
  onForward,
}: {
  id: string;
  folders: MailFolder[];
  position: number;
  total: number;
  onChanged: () => void;
  onClosed: () => void;
  onNewer: () => void;
  onOlder: () => void;
  onReply: (email: EmailDetail, draft?: string) => void;
  onForward: (email: EmailDetail) => void;
}) {
  const { t, i18n } = useTranslation();
  const features = useFeatures();
  const entitlements = useEntitlements();
  const queryClient = useQueryClient();
  const [showImages, setShowImages] = useState(false);
  const [instructions, setInstructions] = useState("");
  const ai = useMutation({
    mutationFn: (action: AiMailResult["action"]) =>
      api.mail.ai(id, {
        action,
        ...(action === "draft_reply" && instructions.trim() ? { instructions } : {}),
      }),
  });
  const email = useQuery({ queryKey: ["mail", "email", id], queryFn: () => api.mail.email(id) });
  const update = useMutation({
    mutationFn: (change: { seen?: boolean; flagged?: boolean; mailboxId?: string }) =>
      api.mail.update(id, change),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["mail", "email", id] });
      onChanged();
    },
  });

  // Mark read once when an unread message loads.
  const unseenId = email.data && !email.data.seen ? email.data.id : null;
  const markRead = update.mutate;
  const online = useOnline();
  useEffect(() => {
    // Offline copies are read-only; the message is marked read the next time it is opened online.
    if (unseenId && online) markRead({ seen: true });
  }, [unseenId, markRead, online]);

  const e = email.data;
  const archive = folders.find((f) => f.role === "archive");
  const trash = folders.find((f) => f.role === "trash");
  const mutate = update.mutate;
  const reply = useCallback(() => e && onReply(e), [e, onReply]);
  const flag = useCallback(() => e && mutate({ flagged: !e.flagged }), [e, mutate]);
  const unread = useCallback(() => {
    mutate({ seen: false });
    onClosed();
  }, [mutate, onClosed]);
  const moveTo = useCallback(
    (folder: MailFolder | undefined) => {
      if (!folder) return;
      mutate({ mailboxId: folder.id });
      onClosed();
    },
    [mutate, onClosed],
  );

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && EDITABLE.test(target.tagName)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const actions: Record<string, () => void> = {
        r: reply,
        s: flag,
        u: unread,
        f: () => e && onForward(e),
        e: () => moveTo(archive),
        "#": () => moveTo(trash),
      };
      const action = actions[event.key];
      if (action) {
        event.preventDefault();
        action();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [reply, flag, unread, moveTo, archive, trash, e, onForward]);

  const toolbar = (
    <div className="sticky top-0 z-10 flex h-12 shrink-0 items-center gap-0.5 bg-surface px-2">
      <ToolbarButton label={t("mail.back")} icon={ArrowLeft} onClick={onClosed} />
      <span className="mx-1 hidden h-5 w-px bg-rule md:block" />
      {archive ? (
        <ToolbarButton label={t("mail.archive")} icon={Archive} onClick={() => moveTo(archive)} />
      ) : null}
      {trash ? (
        <ToolbarButton label={t("mail.delete")} icon={Trash2} onClick={() => moveTo(trash)} />
      ) : null}
      <ToolbarButton label={t("mail.markUnread")} icon={Mail} onClick={unread} />
      <span className="flex-1" />
      {position >= 0 ? (
        <span className="hidden items-center gap-1 text-xs text-ink-3 md:flex">
          {t("mail.position", { position: position + 1, total })}
          <ToolbarButton
            label={t("mail.newer")}
            icon={ArrowLeft}
            onClick={onNewer}
            disabled={position === 0}
          />
          <span className="rotate-180">
            <ToolbarButton
              label={t("mail.older")}
              icon={ArrowLeft}
              onClick={onOlder}
              disabled={position >= total - 1}
            />
          </span>
        </span>
      ) : null}
    </div>
  );

  if (!e) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {toolbar}
        <div className="p-6 text-sm text-ink-3">
          {email.error ? <ErrorNotice error={email.error} /> : t("common.loading")}
        </div>
      </div>
    );
  }
  const who = senderOf(e);
  const fullDate = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(e.receivedAt));
  const recipients = [...e.to, ...e.cc].map((a) => a.name || a.email).join(", ");

  return (
    <article className="flex min-h-0 flex-1 flex-col overflow-auto">
      {toolbar}
      <div className="px-4 pb-24 md:px-6 md:pb-10">
        <div className="flex items-start gap-2 pt-2 md:pl-14">
          <h1 className="min-w-0 flex-1 text-[22px] leading-snug break-words text-ink">
            {e.subject || t("mail.noSubject")}
          </h1>
          <button
            type="button"
            aria-label={e.flagged ? t("mail.unstar") : t("mail.star")}
            aria-pressed={e.flagged}
            className="flex size-10 shrink-0 items-center justify-center rounded-full hover:bg-ink/5"
            onClick={flag}
          >
            <Star
              className={`size-5 ${e.flagged ? "fill-[#f4b400] text-[#f4b400]" : "text-ink-3"}`}
            />
          </button>
        </div>

        <div className="mt-4 flex items-start gap-3">
          <Avatar name={who} size={40} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-semibold text-ink">{who}</span>
              <span className="truncate text-xs text-ink-3">&lt;{e.from[0]?.email}&gt;</span>
            </div>
            <div className="truncate text-xs text-ink-3">{t("mail.toLine", { recipients })}</div>
          </div>
          <span className="hidden shrink-0 pt-1 text-xs text-ink-3 sm:block">{fullDate}</span>
          <ToolbarButton label={t("mail.reply")} icon={Reply} onClick={reply} />
        </div>

        <div className="mt-4 flex flex-wrap gap-2 md:pl-[52px]">
          {features.ai ? (
            <>
              <ChipButton
                icon={Sparkles}
                label={t("mail.summarize")}
                onClick={() => ai.mutate("summarize")}
                disabled={ai.isPending}
              />
              <ChipButton
                icon={Pencil}
                label={t("mail.draftReply")}
                onClick={() => ai.mutate("draft_reply")}
                disabled={ai.isPending}
              />
            </>
          ) : null}
          <AddTask email={e} />
          {entitlements.data?.features.notes ? <SaveAsNote email={e} /> : null}
        </div>
        <ErrorNotice error={update.error} />
        <AssistantCard
          result={ai.data}
          error={ai.error}
          pending={ai.isPending}
          instructions={instructions}
          onInstructions={setInstructions}
          onRedraft={() => ai.mutate("draft_reply")}
          onUseDraft={(text) => onReply(e, text)}
        />

        <div className="mt-6 md:pl-[52px]">
          {e.htmlBody ? (
            <HtmlBody
              html={e.htmlBody}
              showImages={showImages}
              onShowImages={() => setShowImages(true)}
            />
          ) : (
            <pre className="font-sans text-sm leading-relaxed whitespace-pre-wrap break-words text-ink">
              {e.textBody ?? ""}
            </pre>
          )}
        </div>

        {e.attachments.length > 0 ? (
          <div className="mt-6 border-t border-rule pt-4 md:ml-[52px]">
            <div className="mb-3 text-sm font-medium text-ink-2">
              {t("mail.attachmentCount", { count: e.attachments.length })}
            </div>
            <div className="flex flex-wrap gap-3">
              {e.attachments.map((a) => (
                <a
                  key={a.blobId}
                  href={api.mail.attachmentUrl(e.id, a.blobId, a.name)}
                  download={a.name ?? undefined}
                  className="group flex w-56 items-center gap-3 rounded-xl border border-rule p-3 hover:bg-surface-2"
                >
                  <FileText className="size-8 shrink-0 text-danger" strokeWidth={1.5} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-ink">{a.name ?? a.type}</span>
                    <span className="block text-xs text-ink-3">
                      {Math.max(1, Math.round(a.size / 1024))} KB
                    </span>
                  </span>
                  <Download className="size-4 shrink-0 text-ink-3 group-hover:text-ink" />
                </a>
              ))}
            </div>
          </div>
        ) : null}

        <div className="mt-8 flex flex-wrap gap-3 md:pl-[52px]">
          <button
            type="button"
            onClick={reply}
            className="flex h-10 items-center gap-2 rounded-full border border-ink-3/50 px-5 text-sm text-ink-2 hover:bg-surface-2"
          >
            <Reply className="size-4" />
            {t("mail.reply")}
          </button>
          <button
            type="button"
            onClick={() => onForward(e)}
            className="flex h-10 items-center gap-2 rounded-full border border-ink-3/50 px-5 text-sm text-ink-2 hover:bg-surface-2"
          >
            <Forward className="size-4" />
            {t("mail.forward")}
          </button>
        </div>
      </div>
    </article>
  );
}

function ChipButton({
  icon: IconComponent,
  label,
  onClick,
  disabled = false,
}: {
  icon: Icon;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex h-8 items-center gap-1.5 rounded-lg border border-rule px-3 text-xs text-ink-2 hover:bg-surface-2 disabled:opacity-50"
    >
      <IconComponent className="size-4" />
      {label}
    </button>
  );
}

/** Saves the message text as a note, quoted, with a link back to the message. */
function SaveAsNote({ email }: { email: EmailDetail }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: () => {
      const from = senderOf(email);
      const quoted = (email.textBody ?? "")
        .trim()
        .split("\n")
        .map((line) => `> ${line}`)
        .join("\n");
      return api.notes.create({
        title: email.subject || t("tasks.fromEmail"),
        body: `${from ? `${t("notes.fromSender", { from })}\n\n` : ""}${quoted}\n`,
        email: { id: email.id, subject: email.subject },
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notes"] }),
  });
  if (save.data) {
    return (
      <Link
        to="/notes"
        search={{ note: save.data.id }}
        className="flex h-8 items-center gap-1.5 rounded-lg bg-good-soft px-3 text-xs text-good"
      >
        <StickyNote className="size-4" />
        {t("notes.saved")}
      </Link>
    );
  }
  return (
    <>
      <ChipButton
        icon={StickyNote}
        label={t("notes.saveFromEmail")}
        onClick={() => save.mutate()}
        disabled={save.isPending}
      />
      <ErrorNotice error={save.error} />
    </>
  );
}

/** Turns the open message into a task in the default list, linked back to the message. */
function AddTask({ email }: { email: EmailDetail }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const add = useMutation({
    mutationFn: async () => {
      const { lists } = await queryClient.fetchQuery({
        queryKey: ["tasks", "lists"],
        queryFn: () => api.tasks.lists(),
      });
      const list = lists.find((l) => l.isDefault) ?? lists[0];
      if (!list) throw new Error("No task list");
      return api.tasks.create({
        listId: list.id,
        title: email.subject || t("tasks.fromEmail"),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        email: { id: email.id, subject: email.subject },
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tasks"] }),
  });
  if (add.data) {
    return (
      <Link
        to="/tasks"
        search={{ list: add.data.listId, task: add.data.id }}
        className="flex h-8 items-center gap-1.5 rounded-lg bg-good-soft px-3 text-xs text-good"
      >
        <ListChecks className="size-4" />
        {t("tasks.added")}
      </Link>
    );
  }
  return (
    <>
      <ChipButton
        icon={ListChecks}
        label={t("tasks.addFromEmail")}
        onClick={() => add.mutate()}
        disabled={add.isPending}
      />
      <ErrorNotice error={add.error} />
    </>
  );
}

/** The assistant's answer, shown for review; a reply draft is never sent by itself. */
function AssistantCard({
  result,
  error,
  pending,
  instructions,
  onInstructions,
  onRedraft,
  onUseDraft,
}: {
  result: AiMailResult | undefined;
  error: unknown;
  pending: boolean;
  instructions: string;
  onInstructions: (value: string) => void;
  onRedraft: () => void;
  onUseDraft: (text: string) => void;
}) {
  const { t } = useTranslation();
  if (!pending && !result && !error) return null;
  return (
    <section className="mt-4 rounded-2xl bg-surface-2 p-4 text-sm md:ml-[52px]">
      <div className="flex items-center gap-2 text-xs font-medium text-ink-2">
        <Sparkles className="size-4 text-accent" />
        {result?.action === "draft_reply" ? t("mail.draftReply") : t("mail.summarize")}
      </div>
      {pending ? <p className="mt-2 text-ink-2">{t("mail.aiWorking")}</p> : null}
      {error ? (
        <div className="mt-2">
          <ErrorNotice error={error} />
        </div>
      ) : null}
      {result && !pending ? (
        <>
          <pre className="mt-2 font-sans text-sm leading-relaxed whitespace-pre-wrap text-ink">
            {result.text}
          </pre>
          {result.action === "draft_reply" ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input
                className="h-9 min-w-48 flex-1 rounded-full border border-rule bg-surface px-4 text-sm outline-none focus:border-accent"
                placeholder={t("mail.aiInstructions")}
                value={instructions}
                maxLength={500}
                onChange={(event) => onInstructions(event.target.value)}
              />
              <button
                type="button"
                className="h-9 rounded-full border border-rule px-4 text-sm text-ink-2 hover:bg-surface"
                onClick={onRedraft}
              >
                {t("mail.redraft")}
              </button>
              <button
                type="button"
                className="h-9 rounded-full bg-accent px-4 text-sm font-medium text-white"
                onClick={() => onUseDraft(result.text)}
              >
                {t("mail.useDraft")}
              </button>
            </div>
          ) : null}
          <p className="mt-3 text-xs text-ink-3">
            {t("mail.aiRemaining", { count: result.remainingActions })} · {t("mail.aiNote")}
          </p>
        </>
      ) : null}
    </section>
  );
}

/**
 * HTML mail is sanitised with DOMPurify and shown in a sandboxed iframe (no scripts, no forms,
 * no navigation). Remote images are stripped until the reader asks for them, so senders cannot
 * track opens by default.
 */
function HtmlBody({
  html,
  showImages,
  onShowImages,
}: {
  html: string;
  showImages: boolean;
  onShowImages: () => void;
}) {
  const { t } = useTranslation();
  const [height, setHeight] = useState(200);
  const frame = useRef<HTMLIFrameElement>(null);
  const doc = useMemo(() => {
    const clean = DOMPurify.sanitize(html, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ["style", "form", "input", "button", "iframe", "object", "embed"],
      FORBID_ATTR: ["srcset"],
    });
    const body = showImages ? clean : clean.replace(/<img\b[^>]*>/gi, "");
    return `<!doctype html><meta charset="utf-8"><meta name="referrer" content="no-referrer"><base target="_blank"><style>body{font:14px/1.6 system-ui,sans-serif;margin:0;color:#1f1f1f;background:#fff;word-break:break-word}img{max-width:100%}a{color:#0f6e6b}</style><body>${body}</body>`;
  }, [html, showImages]);
  const blockedImages = !showImages && /<img\b/i.test(html);
  return (
    <div className="flex flex-col gap-2">
      {blockedImages ? (
        <div className="flex items-center justify-between rounded-lg bg-surface-2 px-3 py-2 text-xs text-ink-2">
          <span>{t("mail.imagesBlocked")}</span>
          <button
            type="button"
            className="font-medium text-accent hover:underline"
            onClick={onShowImages}
          >
            {t("mail.images")}
          </button>
        </div>
      ) : null}
      <iframe
        ref={frame}
        title="message"
        sandbox="allow-popups allow-popups-to-escape-sandbox"
        srcDoc={doc}
        className="w-full rounded-lg bg-white"
        style={{ height }}
        onLoad={() => {
          const h = frame.current?.contentDocument?.documentElement?.scrollHeight;
          if (h) setHeight(Math.min(Math.max(h + 16, 120), 4000));
        }}
      />
    </div>
  );
}
